import {SRGBColorSpace, Timer, Vector3, type Texture} from 'three'

import {createAudioUnlock, type AudioUnlock} from '../audio/audio-unlock'
import type {MuteState} from '../audio/mute-state'
import {createSoundBank, describeSoundWarning, type SoundBank} from '../audio/sound-bank'
import {
  BLINK_INTERVAL_MAX_SECONDS,
  BLINK_INTERVAL_MIN_SECONDS,
  CAMERA_HEIGHT_M,
  EFFECT_TOTAL_SECONDS,
  ENTRANCE_STEP_WEIGHTS,
  LEADIN_STEP_COUNT,
  LEADIN_STRIDE_RATIO,
  MANIFEST_URL,
  SHADOW_CATCHER_SCALE,
  UI_TEXT,
} from '../config'
import {loadManifest} from '../content/manifest'
import {createEntranceEffect, describeEffectWarning, type EntranceEffect} from '../effects/entrance-effect'
import {createPawTexture} from '../effects/paw-mask'
import {
  createModelHitTarget,
  describeModelHitWarning,
  type ModelHitTarget,
} from '../interaction/model-hit'
import {createAnimationController, type AnimationController} from '../model/animation'
import {loadModel} from '../model/loader'
import {describeColorWarning, resolveAccentColor} from '../model/model-color'
import {describeWarning, normalizeModel} from '../model/normalize'
import {createHitTestService, type HitTestService, type PlacementResult} from '../placement/hit-test'
import {createPlacementController, type PlacementController} from '../placement/placement-controller'
import {
  createPlacementSequence,
  describeSequenceWarning,
  type PlacementSequence,
} from '../placement/placement-sequence'
import {createGroundPlane} from '../scene/ground'
import {createShadowCatcher, setupLighting, type Lighting} from '../scene/lighting'
import {createCaptureUi, type CaptureUi} from '../ui/capture-ui'
import type {Hud} from '../ui/hud'
import {AppError, describeError} from '../util/errors'
import {createXr8Facade, type Xr8Facade} from './xr8-facade'

const MODULE_NAME = 'campaign-ar'

/** Emitted by the engine's `reality` module. Source: `src/tracking-controller.ts`. */
const TRACKING_STATUS_EVENT = 'reality.trackingstatus'

const isDebug = (): boolean => new URLSearchParams(window.location.search).has('debug')

/** Stand-in for the camera position in the debug readout before `onStart`. */
const ORIGIN = new Vector3()

export interface AppModuleDeps {
  readonly xr8: Xr8Global
  readonly hud: Hud
  /**
   * Owned by main.ts; written by the consent dialog and the mute toggle. Only
   * threaded through to the sound bank - this module holds no mute logic, so
   * the toggle keeps working across detach/attach without touching it.
   */
  readonly muteState: MuteState
}

/**
 * Our single custom camera pipeline module. Everything app-specific hangs off
 * its lifecycle callbacks; the engine owns the frame loop.
 */
export const createAppModule = ({xr8, hud, muteState}: AppModuleDeps): Xr8PipelineModule => {
  let facade: Xr8Facade | null = null
  let lighting: Lighting | null = null
  let hitTest: HitTestService | null = null
  let placement: PlacementController | null = null
  let animation: AnimationController | null = null
  let entrance: EntranceEffect | null = null
  let sequence: PlacementSequence | null = null
  /** Owned here, borrowed by the lead-in trail. */
  let pawMask: Texture | null = null
  let modelHit: ModelHitTarget | null = null
  let sounds: SoundBank | null = null
  let captureUi: CaptureUi | null = null
  let timer: Timer | null = null
  let canvasElement: HTMLCanvasElement | null = null
  const abort = new AbortController()

  let audioUnlock: AudioUnlock | null = null
  /**
   * The one AudioContext.
   *
   * A thunk rather than a value so that a campaign which configures no sounds
   * never reaches it: `SoundBank.load` calls this only after it has found at
   * least one URL to fetch. `createAudioUnlock` installs five capture-phase
   * window listeners that stay until the context is running, and a silent
   * campaign should pay for none of that.
   */
  const getAudioUnlock = (): AudioUnlock => (audioUnlock ??= createAudioUnlock(abort.signal))

  let trackingReady = false
  let modelReady = false
  /**
   * The last thing the per-frame ground probe returned, kept only so `?debug=1`
   * can report it.
   *
   * The probe itself runs for the ground-height median (see `hit-test.ts`), not
   * for this. But it is also the only observable answer to "would a tap here
   * land, and via which arm of the hit test" - and the AR path runs on a device
   * and nowhere else, so an unobservable answer is no answer.
   */
  let lastGround: PlacementResult | null = null

  /**
   * A tap is only meaningful once SLAM has converged AND the model has arrived.
   * Placing before the model exists would commit an empty Group and leave the
   * user staring at a spot where nothing appears.
   */
  const refreshHint = (): void => {
    if (placement?.isPlaced === true) return
    if (!trackingReady) hud.setText(UI_TEXT.initializing)
    else if (!modelReady) hud.setText(UI_TEXT.preparing)
    else hud.setText(UI_TEXT.readyToPlace)
  }

  /**
   * The placement beat: the character arrives, here and now.
   *
   * The four calls below are the invariant the README describes. They have to
   * restart on ONE frame together, or the clip, the light and the sound drift
   * out of phase with each other.
   *
   * Extracted into a named function for exactly one reason: so the block can be
   * SCHEDULED without being taken apart. A lead-in delays the call to this
   * function; it never delays a line inside it. The block used to be held
   * together by being adjacent, which was a convention a refactor could break
   * silently - now there is one call site in this file, and that is the
   * invariant made structural.
   *
   * CONTRACT: nothing in here may call back into `PlacementSequence`. `start()`
   * invokes this synchronously when no lead-in is configured, so a call back
   * would re-enter and never terminate.
   */
  const revealPlaced = (): void => {
    placement?.reveal()
    animation?.playIntro()
    entrance?.play()
    // A no-op when the campaign configured no `audio.arrive` or the file failed
    // to load. On the very first tap of an iOS session it queues instead, until
    // the matching pointerup unlocks the context a few tens of milliseconds
    // later - though with a lead-in configured that has already happened.
    sounds?.play('arrive')
    // Below the beat, never inside it. Those four restart together by contract;
    // these are unrelated bookkeeping and must not look like part of it.
    //
    // Gated with the reveal rather than with the tap so the shutter cannot hand
    // back a photo of an empty floor during a lead-in. `setAvailable` is
    // idempotent and only ever called with `true`, so a re-placement does not
    // flicker the button.
    captureUi?.setAvailable(true)
    // The reaction is the one affordance nothing on screen advertises - the
    // shutter is a circle that reads as a shutter on its own. So the hint spends
    // its width on the invisible half, but only when there is a clip to find.
    hud.setText(
      (animation?.reactionClips.length ?? 0) > 0 ? UI_TEXT.placedReactive : UI_TEXT.placed
    )
  }

  /**
   * `window.__ar` under `?debug=1`. The AR counterpart of /debug's `__probe`.
   *
   * A getter rather than a periodic `console.log`: the interesting question is
   * always "what is true right now, while I am holding the phone like this",
   * and a log line answers it for a moment that has already passed - besides
   * flooding an inspector attached over USB.
   *
   * It exists because the AR path cannot run anywhere but on a device, and
   * /debug cannot stand in for it: that harness feeds the placement code fixed
   * inputs and never exercises SLAM at all. A floor decal once shipped with
   * seven passing desktop checks and turned out to be invisible on a phone.
   * This is the window that would have caught it.
   */
  const exposeArProbe = (): void => {
    Object.defineProperty(window, '__ar', {
      value: {
        get read() {
          return {
            trackingReady,
            modelReady,
            isPlaced: placement?.isPlaced ?? false,
            // The single most diagnostic value here: what a tap at the centre of
            // the screen would resolve to, and which arm of hit-test.ts answered.
            //   ESTIMATED_SURFACE - SLAM fitted a plane, the happy path, and the
            //                       only kind that feeds the ground-height median
            //   FEATURE_POINT     - SLAM has points but no plane
            //   GROUND_FALLBACK   - hit test returned nothing, the geometric
            //                       floor answered
            //   null              - the screen centre is above the horizon, or
            //                       past MAX_PLACE_DISTANCE_M
            ground:
              lastGround === null
                ? null
                : {
                    source: lastGround.source,
                    y: Number(lastGround.position.y.toFixed(3)),
                    distance: Number(
                      lastGround.position.distanceTo(facade?.camera.position ?? ORIGIN).toFixed(3)
                    ),
                  },
            leadIn: sequence?.debug ?? null,
            // The one-shot `?debug=1` dump above answers "is there a clip at
            // all"; this answers "where is it right now". Added when the
            // greeting arrived, because it is the first animation behaviour
            // whose correctness is a question about TIMING - on a device this is
            // the difference between "the bow never fired" and "the countdown
            // never advanced".
            animation: animation?.debug ?? null,
          }
        },
      },
      configurable: true,
    })
  }

  /**
   * Why a tap did not reach the character. `?debug=1` only.
   *
   * Every term of the gate below, read back after the fact. It exists because
   * the failure it explains is invisible from the outside: a tap that misses the
   * character and a tap that hits it during the entrance both used to look like
   * "it moved instead of reacting", and the only log in here fired on success -
   * so the interesting case produced no output at all.
   *
   * Costs a second raycast, which is the whole reason it is behind the flag.
   * Nothing here may have a side effect: it runs after the decision, and reading
   * it must not be able to change it.
   */
  const diagnoseTap = (event: PointerEvent): Record<string, unknown> => ({
    placed: placement?.isPlaced === true,
    reactionClips: animation?.reactionClips.length ?? 0,
    leadingIn: sequence?.isLeadingIn === true,
    introPlaying: animation?.isIntroPlaying === true,
    hitModel: modelHit?.test(event.clientX, event.clientY) === true,
  })

  const onPointerDown = (event: PointerEvent): void => {
    // Audio unlock deliberately does NOT happen here. `pointerdown` does not
    // reliably carry user activation on touch (the matching `pointerup` does),
    // and the unlock listeners in src/audio/audio-unlock.ts sit on window in the
    // CAPTURE phase - so they have already seen this event, including the taps
    // that fall through the early returns below.

    // Ignore secondary touches so a pinch does not also place the model.
    if (!event.isPrimary || !facade || !hitTest || !placement) return
    if (!modelReady) return

    // Before `hitTest.resolve`, and it must be: every tap that reaches it
    // re-places the model, so a tap on the character has to leave that path here
    // or poking it would teleport it. An early return, deliberately NOT woven
    // into the three-line beat below - that block is about placement.
    //
    // `isPlaced` is load-bearing, not defensive. three's Raycaster does not test
    // `Object3D.visible` (r185, core/Raycaster.js `intersect()` checks layers and
    // nothing else), so `placement.root.visible === false` would not stop the
    // proxy being hit before the first placement. Falling through is also the
    // right answer there: there is nothing on screen to react yet.
    //
    // The empty-pool check is load-bearing too. A GLB with no reaction clip must
    // keep its whole silhouette as placement surface rather than becoming a dead
    // zone - the same posture `intro` takes: no clip, no behaviour, no guessing.
    // `reactionClips` is already filtered to clips that exist in the GLB, so a
    // campaign whose every reaction name is a typo lands here rather than on a
    // silhouette that swallows taps and plays nothing.
    //
    // WHAT THIS BLOCK ASKS IS "did the tap land on the character", NOT "can it
    // react right now". Those are different questions and conflating them is a
    // bug this code has already shipped once.
    //
    // `isIntroPlaying` used to be a fourth condition here, so a tap during the
    // entrance fell through and re-placed the model. The reasoning was that
    // reacting is not on offer during the intro (`playReaction` declines for
    // reasons of its own), leaving a choice between re-placing and swallowing,
    // and that re-placing is the one that answers the finger. That was true when
    // `Appear` ran 1.25 s. It inverted when the bow was baked into the clip and
    // it became 3.25 s, because re-placing is not a neutral answer - it restarts
    // the lead-in, which 0.58 s later restarts `playIntro`, which re-arms the
    // very window that sent the tap here. 0.58 + 3.25 = 3.83 s during which
    // every poke teleports the character and buys another 3.83 s. A user doing
    // the natural thing - tapping again because nothing happened - never leaves
    // it, and the character reads as having no reaction at all.
    //
    // So the intro check belongs where its reason lives, inside `playReaction`,
    // and a tap that hit the character stops here either way. The cost is that
    // poking mid-entrance now does nothing visible; the alternative was doing
    // something actively wrong.
    //
    // Fires on `pointerdown` rather than a `pointerup` tap gate, matching
    // placement: two gestures on the same surface with different latency reads
    // worse than the one thing a down-only trigger gets wrong, which is the
    // first finger of a pinch. That misfire costs a 1.25 s additive layer with
    // no other side effects. Revisit when drag/pinch land.
    if (
      placement.isPlaced &&
      animation !== null &&
      animation.reactionClips.length > 0 &&
      // Stronger than the entrance case, and the reason it stays: during a
      // lead-in the character is not merely mid-entrance, it is not on screen at
      // all, while the proxy already stands where it is going to be. `Raycaster`
      // ignores `visible` (see README), so without this a tap on the trail hits
      // the proxy and the tap does nothing - there is no character there to poke
      // yet, and placement is the only sensible reading of it.
      sequence?.isLeadingIn !== true &&
      // Last, because it is the only one that costs a raycast. A fifth term goes
      // in a named predicate rather than on the end of this.
      modelHit?.test(event.clientX, event.clientY) === true
    ) {
      // No entrance effect, no hint change, and never the ARRIVE sound: poking
      // the character has to LOOK different from placing it, and placement owns
      // the ring, the blip and the line of copy. What the tap gets instead is
      // its own voice - the react cue keyed to whichever clip the draw landed
      // on - so the two gestures stay distinguishable by ear as well as by eye.
      //
      // The cue hangs off the returned clip name rather than firing alongside
      // the call, so a declined tap stays silent by construction instead of by
      // a second copy of playReaction's guards. `null` is an ordinary outcome,
      // not a failure: the intro is running, or a reaction already is. Either
      // way the tap was spent on the character. A clip with no entry in
      // `audio.react` reacts silently, which is a configuration, not a bug.
      const clip = animation.playReaction()
      if (clip !== null) sounds?.playReaction(clip)
      if (isDebug()) console.info(`[${MODULE_NAME}] tap`, {target: 'model', clip})
      return
    }

    const result = hitTest.resolve(event.clientX, event.clientY)
    if (!result) {
      // Above the horizon, or implausibly far. Silent to the user - but not to
      // `?debug=1`, because "I tapped and nothing happened" is one of the
      // reports this readout exists to answer.
      if (isDebug()) console.info(`[${MODULE_NAME}] tap`, {target: 'none', ...diagnoseTap(event)})
      return
    }
    if (isDebug()) console.info(`[${MODULE_NAME}] tap`, {target: 'floor', ...diagnoseTap(event)})

    placement.place(result.position, facade.camera.position)
    // Before the reveal, not after: the shadow frustum has to be centred on the
    // new spot by the time anything is drawn there. Cheap and idempotent, so a
    // re-placement mid-lead-in simply re-aims it.
    lighting?.followTarget(placement.root)

    // The only entry point, whether or not a lead-in is configured: with
    // `leadIn: 'none'` the sequencer calls `revealPlaced` synchronously, so the
    // frame timing is identical to what it was before it existed. The `null`
    // branch is for the window between the first tap being possible and
    // `loadContent` finishing - `modelReady` already rules that out, but relying
    // on that from here would be relying on it from two places.
    if (sequence === null) revealPlaced()
    else {
      // Only reached with a lead-in, since otherwise `start()` overwrites this
      // in the same turn.
      hud.setText(UI_TEXT.arriving)
      sequence.start()
    }
  }

  /**
   * Loads the manifest and the model behind the already-running camera feed.
   *
   * Deliberately not awaited by `onStart`: blocking AR startup on a model
   * download means a black screen for however long the network takes, when we
   * could be showing the camera and the "find the floor" prompt instead.
   */
  const loadContent = async (active: Xr8Facade): Promise<void> => {
    const manifest = await loadManifest(MANIFEST_URL, abort.signal)

    // Started here and deliberately NOT awaited into the model's chain: it is
    // tens of KB against the model's hundreds, and starting now puts the audio
    // unlock listeners on window long before a tap can happen. `load` never
    // rejects - it resolves with warnings - which is what makes it structurally
    // impossible for a missing sound file to reach the catch in `onStart` and
    // replace the on-screen hint with an error message.
    const soundWarnings = sounds?.load(manifest.audio, abort.signal) ?? Promise.resolve([])

    // The environment map choice arrives with the manifest, after the scene is
    // already up. Rebuilding through `setupLighting` keeps it the single place
    // that knows how the scene is lit, rather than patching from two.
    lighting?.dispose()
    lighting = setupLighting(active.scene, active.renderer, manifest.scene.environment)

    const gltf = await loadModel(active.renderer, manifest.model.url, abort.signal)

    // Order matters: normalise BEFORE creating the mixer. three.js computes a
    // SkinnedMesh's bounding box by skinning every vertex in its CURRENT pose
    // and caches the result forever, so measuring after the animation starts
    // would size the model from an arbitrary frame.
    const normalized = normalizeModel(gltf.scene, manifest.model)
    animation = createAnimationController(gltf.scene, gltf.animations, manifest.model.animation)

    // Under the PIVOT, not the placement root - which is the only reason this
    // can sit above `setContent` while the two blocks below have to sit under
    // it. `root.clear()` empties the root; the pivot is what gets cleared INTO,
    // so anything parented to it comes along.
    //
    // Sized from `normalized.localSize`, i.e. the same Box3 measurement taken
    // above. Re-measuring here would have to run AFTER the mixer exists, and
    // three caches a SkinnedMesh's bounds from whatever pose it is in - the
    // exact ordering the comment above forbids.
    modelHit = createModelHitTarget({
      camera: active.camera,
      // The renderer's own element, not `onStart`'s canvas argument. They are
      // the same object; this one is the element NDC is defined against.
      canvas: active.renderer.domElement,
      localSize: normalized.localSize,
    })
    normalized.pivot.add(modelHit.proxy)

    placement?.setContent(normalized.pivot)

    // Both of these must be added AFTER `setContent`: it calls `root.clear()`,
    // which would silently take them with it.
    const shadowCatcher = createShadowCatcher(
      Math.max(normalized.groundRadius, 0.05) * SHADOW_CATCHER_SCALE
    )
    placement?.root.add(shadowCatcher)

    // Synchronous: GLTFLoader's onLoad does not fire until every texture promise
    // has settled, so materials and images are fully resolved by now.
    const accent = resolveAccentColor(gltf.scene, manifest.placement)

    entrance = createEntranceEffect({
      renderer: active.renderer,
      config: manifest.placement,
      accentColor: accent.color,
      groundRadius: normalized.groundRadius,
      shadowCatcher,
    })
    placement?.root.add(entrance.root)

    // Built here rather than lazily on the first tap: generating it costs a
    // canvas and one fill, and doing that inside the tap frame would hitch
    // exactly the frame the user is judging.
    pawMask = createPawTexture(active.renderer)

    // Also after `setContent`, for the same `root.clear()` reason as above.
    sequence = createPlacementSequence({
      config: manifest.placement,
      mask: pawMask,
      accentColor: accent.color,
      groundRadius: normalized.groundRadius,
      hasIntro: animation.introClipName !== null,
      onReveal: revealPlaced,
      // Not part of `revealPlaced`: these fire during the lead-in, and the beat
      // sheet they belong to is the sequencer's, not the placement beat's.
      // A print with no weight gets no sound rather than the last one's.
      onStep: (index) => {
        const weight = ENTRANCE_STEP_WEIGHTS[index]
        if (weight !== undefined) sounds?.play('step', weight)
      },
    })
    placement?.root.add(sequence.root)

    for (const warning of normalized.warnings) {
      console.warn(`[${MODULE_NAME}] model: ${describeWarning(warning)}`)
    }
    for (const warning of animation.warnings) {
      console.warn(`[${MODULE_NAME}] animation: ${JSON.stringify(warning)}`)
    }
    for (const warning of entrance.warnings) {
      console.warn(`[${MODULE_NAME}] effect: ${describeEffectWarning(warning)}`)
    }
    for (const warning of sequence.warnings) {
      console.warn(`[${MODULE_NAME}] lead-in: ${describeSequenceWarning(warning)}`)
    }
    for (const warning of modelHit.warnings) {
      console.warn(`[${MODULE_NAME}] tap target: ${describeModelHitWarning(warning)}`)
    }
    for (const warning of accent.warnings) {
      console.warn(`[${MODULE_NAME}] color: ${describeColorWarning(warning)}`)
    }

    if (isDebug()) {
      console.info(`[${MODULE_NAME}] manifest`, manifest)
      console.info(`[${MODULE_NAME}] model`, {
        bytes: gltf.byteLength,
        decoders: gltf.decoders.length > 0 ? gltf.decoders : '(none needed)',
        measuredHeight: normalized.measuredHeight,
        appliedScale: normalized.appliedScale,
        groundRadius: normalized.groundRadius,
        clipName: animation.clipName,
        clipDuration: animation.clipDuration,
        introClipName: animation.introClipName,
        introDuration: animation.introDuration,
        // Printed next to introDuration on purpose. The entrance effect's
        // timeline is a fixed constant, so a GLB whose intro clip is much
        // shorter or longer than this will visibly desync - and this is the
        // readout that makes that checkable during a model swap.
        effectDuration: EFFECT_TOTAL_SECONDS,
        // Next to the two durations above, because the three of them are the
        // whole timeline of a placement and a swap has to be checkable in one
        // read. `leadInDuration` is how long the user waits before the intro
        // clip even starts, so `leadIn` set with `introClipName: null` is the
        // combination to look for - the lead-in promises an arrival the model
        // has no animation for. `createPlacementSequence` warns about exactly
        // that, but this is where it is visible without reading warnings.
        leadIn: manifest.placement.leadIn,
        leadInDuration: sequence.debug.durationSeconds,
        // How far the trail reaches from the placement point, in metres. The
        // number to sanity-check after a model swap: the whole trail has to stay
        // on floor that is actually flat, and this is the only readout that says
        // how much floor that assumes.
        leadInReachMeters:
          manifest.placement.leadIn === 'none'
            ? 0
            : normalized.groundRadius * LEADIN_STEP_COUNT * LEADIN_STRIDE_RATIO,
        // The three additive layers. A swap that gets the clip NAMES wrong fails
        // silently - the model simply never blinks - so these are the only way
        // to tell "no Blink clip in the GLB" from "the timer never fired".
        blinkClipName: animation.blinkClipName,
        blinkDuration: animation.blinkDuration,
        blinkIntervalSeconds: [BLINK_INTERVAL_MIN_SECONDS, BLINK_INTERVAL_MAX_SECONDS],
        // A list, and an empty one is the "every reaction name is a typo" signal
        // - the same tell as a null `blinkClipName` above, one cardinality up.
        reactionClips: animation.reactionClips,
        greetingClipName: animation.greetingClipName,
        greetingDuration: animation.greetingDuration,
        // Where the greeting lands on the placement timeline, in clip seconds
        // from the reveal. Printed with the durations above because it is
        // DERIVED from introDuration: a GLB whose entrance clip got half a
        // second longer moves the bow with it, silently, and this is the readout
        // that says where it ended up.
        //
        // `greetingClipName: null` with `greetingStartSeconds: 0` is what a
        // campaign with NO greeting block looks like, and is the current
        // reference reading - this model's bow is inside `Appear`.
        greetingStartSeconds: animation.greetingStartSeconds,
        // Printed next to groundRadius's neighbours on purpose: this is the tap
        // volume, so a swap is checkable in one read. If hitExtents is not half
        // of localSize on some axis, the minimum-extent clamp fired and the
        // target is deliberately fatter than the model.
        localSize: normalized.localSize,
        hitExtents: modelHit.extents,
        accentColor: `#${accent.color.getHexString(SRGBColorSpace)}`,
        accentColorSource: accent.source,
        accentSamples: accent.samples,
        // Names here are sanitised by GLTFLoader (dots stripped, collisions
        // suffixed), so they will NOT match the names in the source file.
        nodeNames: gltf.scene.children.flatMap((child) => child.children.map((n) => n.name)),
        drawCalls: active.renderer.info.render.calls,
      })
    }

    modelReady = true
    refreshHint()

    // After the hint flips, so a slow audio fetch can never delay "tap to place".
    for (const warning of await soundWarnings) {
      console.warn(`[${MODULE_NAME}] sound: ${describeSoundWarning(warning)}`)
    }
    if (isDebug()) console.info(`[${MODULE_NAME}] sound`, sounds?.debug)
  }

  return {
    name: MODULE_NAME,

    listeners: [
      {
        event: TRACKING_STATUS_EVENT,
        process: (event) => {
          const detail = (event as Xr8Event<Xr8TrackingStatusDetail>).detail
          const wasReady = trackingReady
          trackingReady = detail.status === 'NORMAL'
          if (trackingReady !== wasReady) refreshHint()
        },
      },
    ],

    onStart: ({canvas}) => {
      canvasElement = canvas
      facade = createXr8Facade(xr8)
      // Dormant: no AudioContext and no listeners until `load()` finds a URL in
      // the manifest, so a campaign with no sound never constructs an audio unit
      // at all.
      sounds = createSoundBank(getAudioUnlock, muteState)
      // Provisional lighting so the scene is never unlit between camera start
      // and manifest arrival; replaced once the manifest says which to use.
      lighting = setupLighting(facade.scene, facade.renderer)

      const ground = createGroundPlane()
      facade.scene.add(ground)
      hitTest = createHitTestService(facade, ground)

      placement = createPlacementController()
      facade.scene.add(placement.root)

      // The camera must start above y = 0 or the ground-plane raycast fallback
      // can never intersect. This height also sets the world scale in the
      // engine's default 'responsive' mode.
      facade.camera.position.set(0, CAMERA_HEIGHT_M, 0)
      facade.syncCameraOrigin()

      // Timer, not Clock: Clock is deprecated as of r183, and connecting to the
      // Page Visibility API clamps the delta when the phone is backgrounded.
      // Without it, returning from a phone call fast-forwards the animation.
      timer = new Timer()
      timer.connect(document)

      // Built at start-up even though the shutter stays hidden until the first
      // placement: a mismatch between index.html and capture-ui.ts then throws
      // here, where RuntimeError surfaces it, instead of on a tap in the field.
      //
      // Nothing pauses the engine while the preview is up. `XR8.pause()` stops
      // the camera, and resuming makes SLAM relocalise - which can move or lose
      // the placed model. Trading a confirmed placement for a few seconds of
      // battery behind an opaque overlay is the wrong way round.
      const activeFacade = facade
      captureUi = createCaptureUi({
        takePhoto: () => activeFacade.captureJpegBase64(),
        // Always wired. Whether anything comes out is `audio.shutter`'s business,
        // and asking that question here would put the answer in two places.
        onShutter: () => sounds?.play('shutter'),
        onError: (error: unknown) => {
          console.error(`[${MODULE_NAME}] capture failed`, describeError(error))
          hud.setText(error instanceof AppError ? error.userMessageJa : UI_TEXT.captureFailed)
        },
        onCaptured: isDebug()
          ? (info) => console.info(`[${MODULE_NAME}] capture`, info)
          : undefined,
      })

      canvas.addEventListener('pointerdown', onPointerDown, {passive: true})
      refreshHint()

      if (isDebug()) exposeArProbe()

      void loadContent(facade).catch((error: unknown) => {
        if (abort.signal.aborted) return
        console.error(`[${MODULE_NAME}] content load failed`, describeError(error))
        hud.setText(
          error instanceof AppError ? error.userMessageJa : 'コンテンツを読み込めませんでした。'
        )
      })
    },

    onUpdate: () => {
      if (timer) {
        timer.update()
        // One local, not two getDelta() calls. Two would in fact be correct -
        // Timer.getDelta() is a pure read of state that only update() advances,
        // which is exactly the Clock flaw it was built to fix - but the
        // animation and the effect MUST advance by the same amount, and a
        // shared local is how that invariant gets stated rather than assumed.
        const delta = timer.getDelta()
        animation?.update(delta)
        entrance?.update(delta)
        // LAST, and this ordering is load-bearing. This update is what fires
        // `revealPlaced()`, and both `playIntro()` and `entrance.play()` are
        // written to apply their own frame 0 immediately (`mixer.update(0)` and
        // `apply()` respectively). Run earlier and that freshly applied frame 0
        // would be advanced by this same frame's delta before anything drew it,
        // costing the ring its brightest frame and Appear its smallest. /debug
        // asserts this: `effect.elapsed` must read exactly 0 on the frame the
        // reveal happens.
        sequence?.update(delta)

        // Keeps the ground-height median fresh, which is what lets
        // `isPlausible` reject feature points on walls and on people. NOT
        // optional and NOT a debug feature: with an empty median every feature
        // point passes unchecked and the model can be placed off the floor. It
        // stops on placement, because from then on the median has no consumer.
        // The return value is only read by `?debug=1`.
        lastGround = placement?.isPlaced !== true ? (hitTest?.sampleGround() ?? null) : null
      }
    },

    onException: (error) => {
      console.error(`[${MODULE_NAME}] pipeline exception`, describeError(error))
    },

    onDetach: () => {
      abort.abort()
      canvasElement?.removeEventListener('pointerdown', onPointerDown)
      canvasElement = null
      animation?.dispose()
      animation = null
      entrance?.dispose()
      entrance = null
      sequence?.dispose()
      sequence = null
      // After its borrower, which is the whole reason it is owned here rather
      // than by the trail itself.
      pawMask?.dispose()
      pawMask = null
      modelHit?.dispose()
      modelHit = null
      // Stops playback and disconnects. Does NOT close the AudioContext -
      // THREE.AudioContext memoises it module-globally, and onDetach can fire
      // while the page lives on.
      sounds?.dispose()
      sounds = null
      // After its borrowers, which is why it is owned out here at all. Removes
      // the window listeners; deliberately does not close the context.
      audioUnlock?.dispose()
      audioUnlock = null
      // Also revokes any object URL still held by the preview.
      captureUi?.dispose()
      captureUi = null
      timer?.disconnect()
      timer = null
      lighting?.dispose()
      lighting = null
      facade = null
      hitTest = null
      placement = null
    },
  }
}
